import React, { useRef, useState } from 'react'
import Box from '@mui/material/Box'
import useCurrentUser from '@/hooks/useCurrentUser'
import { EmtTypography, EmtButton, EmtIcon } from 'emt-components-v2'
import { useIsResponsive } from '@/hooks'
import { useNewRequestCartStore } from '@/store/newRequestCartStore'
import NewSearchForm from '@/components/pages/pet-sitter/search/NewSearchForm'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { Routes } from '@/assets/configs'

// Types
import { Address } from '@/dataV3/Common/Address'
import { FiltersValues } from '@/modules/PetSitter/searchServiceHelper'
import { services } from '@/components/pages/pet-sitter/reservations/NewRequest/requestConfig'
import PublicNavigation from '@/components/EMT/PublicNavigation'

interface ServiceStepComponentProps {
  onNext: (values: FiltersValues) => void
  newSearchInitialStep?: boolean
  setNewSearchInitialStep?: (value: boolean) => void
  loading?: boolean
}

export interface ServiceStepFormValues {
  address: Address
}

const ServiceStep: React.FC<ServiceStepComponentProps> = ({
  onNext,
  newSearchInitialStep,
  setNewSearchInitialStep,
  loading: newSearchLoading,
}) => {
  const { t } = useTranslation('petsitter')
  const { currentUser } = useCurrentUser()
  const router = useRouter()
  const [isMobile] = useIsResponsive()
  const serviceSectionRef = useRef<HTMLDivElement | null>(null)
  const setDistance = useNewRequestCartStore((s) => s.setDistance)
  const setFormSlotsByService = useNewRequestCartStore((s) => s.setFormSlotsByService)
  const formSlotsByService = useNewRequestCartStore((s) => s.formSlotsByService)
  const selectedService = useNewRequestCartStore((s) => s.selectedService)
  const setSelectedService = useNewRequestCartStore((s) => s.setSelectedService)
  const setHasCompleteSearch = useNewRequestCartStore((s) => s.setHasCompleteSearch)

  const [draftSelectedService, setDraftSelectedService] = useState<string | null>(selectedService || 'garde-chez-eux')

  const [loading, setLoading] = useState(false)

  // Add state for slots and refs if you want to use NewSearchForm fully
  const formRefs = useRef<Record<string, any>>({})
  const serviceContentRef = useRef<any>(null)

  const handleSubmit = async () => {
    setLoading(true)

    const validForm = await serviceContentRef.current?.validate?.()
    if (!validForm) {
      setLoading(false)
      return
    }

    const values = serviceContentRef.current.getValues()

    // values attendu (ex): { address, distance, selectedService, formSlots, petsToComplete, ... }

    // ✅ build FiltersValues à envoyer au parent
    const nextFilters: FiltersValues = {
      sortBy: 'recommender',
      address: values.address,
      distance: values.distance,
      services: values.selectedService, // ou tableau selon ton API
      period: values.period, // si présent
      pets: values.petsToComplete
        ? values.petsToComplete.filter((pet: any) => pet.number > 0).map((pet: any) => pet.slug)
        : undefined,
      petSize: values.petSize, // si présent
      features: values.features, // si présent
    }

    setSelectedService(values.selectedService)
    setDistance(values.distance)
    setFormSlotsByService({
      promenade: selectedService === 'promenade' ? values.formSlots : formSlotsByService?.promenade ?? [],
      'garde-chez-vous':
        selectedService === 'garde-chez-vous' ? values.formSlots : formSlotsByService?.['garde-chez-vous'] ?? [],
      'garde-chez-eux':
        selectedService === 'garde-chez-eux' ? values.formSlots : formSlotsByService?.['garde-chez-eux'] ?? [],
      visite: selectedService === 'visite' ? values.formSlots : formSlotsByService?.visite ?? [],
    })

    setLoading(false)
    setHasCompleteSearch(true)

    onNext?.(nextFilters) // ✅ remonte les vraies valeurs
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
      {isMobile && !currentUser && newSearchInitialStep && (
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0 }}>
          <PublicNavigation petsitter />
        </Box>
      )}
      {(!selectedService || newSearchInitialStep) && (
        <>
          <Box className="EmtReactNativeWebViewMarginTop">
            {!currentUser && isMobile && <Box sx={{ height: '80px' }} />}
            <EmtTypography
              variant="body1"
              fontWeight="700"
              color="white"
              lineHeight="1"
              sx={{ textAlign: isMobile ? 'left' : 'center' }}
            >
              {t('search.findPetSitter')}
            </EmtTypography>
            <EmtTypography
              variant="h3"
              fontWeight="800"
              color="white"
              lineHeight="1"
              sx={{ textAlign: isMobile ? 'left' : 'center', marginTop: '13px', fontSize: isMobile ? '24px' : '34px' }}
            >
              {t('reservations.newRequests.labels.serviceNeed')}
            </EmtTypography>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr',
              gridAutoRows: '1fr 1fr',
              flexDirection: isMobile ? 'column' : 'initial',
              gap: '15px',
              maxWidth: '1030px',
              alignSelf: 'center',
            }}
          >
            {services.map((service, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: '10px',
                  backgroundColor: draftSelectedService === service.slug ? '#AE59C6' : '#8A35A2',
                  padding: isMobile ? '10px' : '16px 20px',
                  borderRadius: '16px',
                  border: draftSelectedService === service.slug ? '2px solid #fff' : '2px solid #FFFFFF80',
                  boxShadow: draftSelectedService === service.slug ? '0px 0px 25px 0px #00000040' : 'unset',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    border: '2px solid #fff',
                    boxShadow: '0px 0px 25px 0px #00000040',
                    backgroundColor: '#AE59C6',
                    '& p': {
                      transform: 'scale(1.05)',
                    },
                  },
                }}
                onClick={() => {
                  setDraftSelectedService(service.slug)
                }}
              >
                <EmtIcon
                  className={service.iconClass}
                  sx={{
                    color: '#fff',
                    fontSize: isMobile ? '24px' : '26px',
                  }}
                />
                <EmtTypography
                  variant="body2"
                  fontWeight={'600'}
                  textAlign="center"
                  sx={{ color: '#fff', transform: draftSelectedService === service.slug ? 'scale(1.05)' : 'scale(1)' }}
                >
                  {service.title}
                </EmtTypography>
              </Box>
            ))}
          </Box>
          <Box sx={{ width: '400px', maxWidth: '100%', alignSelf: 'center' }}>
            <EmtButton
              fullWidth
              variant="whiteOutlined"
              color="purple"
              size="large"
              startIcon={<EmtIcon className="emt-search-1" sx={{ fontSize: '20px' }} />}
              onClick={() => {
                setSelectedService(draftSelectedService)
                setNewSearchInitialStep && setNewSearchInitialStep(false)
              }}
            >
              Chercher
            </EmtButton>
          </Box>
        </>
      )}

      {/* Show NewSearchForm only when a service is selected */}
      {selectedService && !newSearchInitialStep && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '30px',
            backgroundColor: isMobile ? 'unset' : '#F6F6F6',
            padding: isMobile ? '0px' : '30px',
            borderRadius: '16px',
            boxShadow: isMobile ? 'unset' : '0px 4px 25px 0px #00000029, 0px -6px 8px 0px #00000014',
            ...(isMobile && { paddingBottom: '110px' }), // hauteur CTA + marge
          }}
        >
          {isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <Box
                onClick={() =>
                  setNewSearchInitialStep && !!currentUser
                    ? setNewSearchInitialStep(true)
                    : router.push(Routes.Connection.WelcomePetsitting)
                }
                sx={{
                  position: 'relative',
                  width: 48,
                  height: 48,
                  borderRadius: '18px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  background: '#fff',
                  cursor: 'pointer',
                  boxShadow: '0px 8px 12px 0px #00000014',
                  flexShrink: 0,
                }}
              >
                <EmtIcon
                  className="emt-arrow-left"
                  sx={{
                    color: 'purple.dark',
                    fontSize: '20px',
                  }}
                />
              </Box>
              <EmtTypography variant="body1" fontWeight="800" lineHeight="1" fontSize="28px">
                Nouvelle recherche
              </EmtTypography>
            </Box>
          )}
          <NewSearchForm
            ref={serviceContentRef}
            isMobile={isMobile}
            selectedService={selectedService}
            formRefs={formRefs}
            localStorageInitialValues={formSlotsByService || null}
            onServiceChange={setSelectedService}
            serviceSectionRef={serviceSectionRef}
          />
          {/* <Box sx={{ paddingBottom: '30px' }}> */}
          <Box sx={{ position: isMobile ? 'sticky' : 'unset', zIndex: 99, bottom: isMobile ? '60px' : 'unset' }}>
            <EmtButton
              fullWidth
              color="purple"
              size="large"
              variant="contained"
              disabled={loading}
              onClick={handleSubmit}
              loader={loading || newSearchLoading}
            >
              Voir les pet sitters
            </EmtButton>
          </Box>
          {/* </Box> */}
        </Box>
      )}
    </Box>
  )
}

export default ServiceStep
